/**
 * Dashboard Index Route
 * Main dashboard overview with algorithms summary
 */

import { createFileRoute, Link } from '@tanstack/react-router'
import { useAuth } from '@/hooks/use-auth'
import { useAppStore } from '@/lib/store'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/trading/card'
import { MetricCard } from '@/components/trading/metric-card'
import { Badge, StatusBadge } from '@/components/trading/badge'
import { Button } from '@/components/ui/button'
import { TradingLineChart } from '@/components/trading/chart'
import { Plus, TrendingUp, Activity, DollarSign, Target } from 'lucide-react'
import { formatNumber, formatPercent } from '@/lib/utils'

export const Route = createFileRoute('/dashboard/')({
  component: DashboardIndex,
})

function DashboardIndex() {
  const { user } = useAuth()
  const { state } = useAppStore()

  // Simulated data (in production, this would come from API)
  const algorithms = state.algorithms.length > 0 ? state.algorithms : [
    {
      id: '1',
      name: 'Trend Following EUR/USD',
      strategy: 'trend-following',
      symbol: 'EUR/USD',
      status: 'active',
      performance: {
        totalReturn: 1250,
        totalReturnPercent: 12.5,
        winRate: 65,
        profitFactor: 2.1,
        sharpeRatio: 1.8,
        maxDrawdown: 8.5,
        totalTrades: 45,
      },
    },
    {
      id: '2',
      name: 'Mean Reversion GBP/USD',
      strategy: 'mean-reversion',
      symbol: 'GBP/USD',
      status: 'inactive',
      performance: {
        totalReturn: 450,
        totalReturnPercent: 4.5,
        winRate: 58,
        profitFactor: 1.6,
        sharpeRatio: 1.2,
        maxDrawdown: 12.3,
        totalTrades: 28,
      },
    },
  ]

  // Portfolio-wide metrics
  const portfolioMetrics = [
    {
      title: 'Total Portfolio Value',
      value: `$${formatNumber(21500)}`,
      change: 1500,
      changeType: 'absolute' as const,
      icon: <DollarSign className="h-6 w-6" />,
      trend: 'up' as const,
    },
    {
      title: 'Total Return',
      value: formatPercent(7.5),
      change: 2.3,
      changeType: 'percent' as const,
      icon: <TrendingUp className="h-6 w-6" />,
      trend: 'up' as const,
    },
    {
      title: 'Active Algorithms',
      value: algorithms.filter((a) => a.status === 'active').length.toString(),
      icon: <Activity className="h-6 w-6" />,
    },
    {
      title: 'Total Trades Today',
      value: '12',
      icon: <Target className="h-6 w-6" />,
    },
  ]

  // Generate portfolio equity curve
  const generateEquityCurve = () => {
    const data = []
    let value = 20000
    for (let i = 30; i >= 0; i--) {
      const date = new Date()
      date.setDate(date.getDate() - i)
      value = value + (Math.random() - 0.45) * 100
      data.push({
        date: date.toLocaleDateString([], { month: 'short', day: 'numeric' }),
        value,
      })
    }
    return data
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            Welcome back, {user?.name || 'Trader'}
          </p>
        </div>
        <Link to="/dashboard/algorithms/new">
          <Button>
            <Plus className="h-4 w-4 mr-2" />
            New Algorithm
          </Button>
        </Link>
      </div>

      {/* Portfolio Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {portfolioMetrics.map((metric) => (
          <MetricCard key={metric.title} {...metric} />
        ))}
      </div>

      {/* Portfolio Equity Curve */}
      <Card>
        <CardHeader>
          <CardTitle>Portfolio Performance</CardTitle>
        </CardHeader>
        <CardContent>
          <TradingLineChart
            data={generateEquityCurve()}
            lines={[
              { dataKey: 'value', name: 'Portfolio Value', color: '#0066cc', strokeWidth: 2 },
            ]}
            xAxisKey="date"
            height={300}
            area={true}
          />
        </CardContent>
      </Card>

      {/* Algorithms List */}
      <div>
        <h2 className="text-xl font-semibold mb-4">Your Algorithms</h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {algorithms.map((algorithm) => (
            <Link key={algorithm.id} to={`/dashboard/algorithms/${algorithm.id}`}>
              <Card className="hover:border-primary/50 cursor-pointer h-full">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{algorithm.name}</CardTitle>
                      <p className="text-sm text-muted-foreground mt-1">
                        {algorithm.symbol} • {algorithm.strategy}
                      </p>
                    </div>
                    <StatusBadge status={algorithm.status} />
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Return: </span>
                      <span className={`font-semibold ${algorithm.performance.totalReturn >= 0 ? 'text-success' : 'text-destructive'}`}>
                        {algorithm.performance.totalReturn >= 0 ? '+' : ''}{formatPercent(algorithm.performance.totalReturnPercent)}
                      </span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Win Rate: </span>
                      <span className="font-semibold">{formatPercent(algorithm.performance.winRate)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Trades: </span>
                      <span className="font-semibold">{algorithm.performance.totalTrades}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Sharpe: </span>
                      <span className="font-semibold">{algorithm.performance.sharpeRatio.toFixed(2)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link to="/dashboard/algorithms">
              <Button variant="outline" className="w-full justify-start">
                <Activity className="h-4 w-4 mr-2" />
                Manage Algorithms
              </Button>
            </Link>
            <Link to="/dashboard/backtests">
              <Button variant="outline" className="w-full justify-start">
                <TrendingUp className="h-4 w-4 mr-2" />
                Run Backtest
              </Button>
            </Link>
            <Link to="/marketplace">
              <Button variant="outline" className="w-full justify-start">
                <Plus className="h-4 w-4 mr-2" />
                Browse Marketplace
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
